import { useEffect, useState } from "react";
import AgentHandlerLinkModalContext from "./context/AgentHandlerLinkModalContext";
import { captureException } from "./telemetry";
import {
  endLinkingFlowAndKickoffInitialSync,
  getNextPage,
  logLinkExecutionRecord,
} from "../utils/fetchUtils";
import {
  NextPageResponse,
  LinkingFlowPageEnum,
  IntegrationCategory,
  NextPagePayloadInfo,
  IntegrationOrCustomIntegration,
  StateHistoryRecord,
  FieldMappingSource,
  FieldMappingPatchRequestBody,
  FieldMappingTarget,
  LinkedAccountFormData,
  RegisteredUserFormData,
  SelectiveSyncConfigurationListRequest,
  LinkingFlowActionEnum,
  LinkedAccountCondition,
  AlreadyIntegratedAction,
  Connector,
  AuthOption,
  MergeLinkOrganizationCustomizationData,
} from "../config/types";
import { closeLinkingFlowWithoutPassingLinkToken, passSuccess } from "../utils/postMessageUtils";
import CustomGlobalStyleWrapper from "../flow/CustomGlobalStyleWrapper";
import { mergeOrgCustomization } from "../utils/customizationUtils";
import PageSwitcher from "./screens/PageSwitcher";
import { resetStepTracking, setLinkingFlowContext } from "./stepTracking";
import { capturePageview } from "./telemetry";

export type RegisteredUserFlowFormData = {
  selected_connector?: Connector;
  selected_auth_option?: AuthOption;
  registered_user_form_data?: RegisteredUserFormData;
};

export type LinkedAccountFlowFormData = {
  selected_category?: IntegrationCategory;
  selected_integration?: IntegrationOrCustomIntegration;
  selected_connector?: Connector;
  selected_auth_option?: AuthOption;
  selected_step_path_id?: string;
  linked_account_form_data?: LinkedAccountFormData;
  fieldMappingSources?: FieldMappingSource[];
  fieldMappingInstances?: FieldMappingPatchRequestBody[];
  fieldMappingTargets?: FieldMappingTarget[];
  skipFieldMappings?: boolean;
  linked_account_id?: string;
  is_successful_flow?: boolean;
  skip_validation_logic?: boolean;
  fix_integration_setup?: boolean;
  selected_already_integrated_action?: AlreadyIntegratedAction;
  selective_sync_selected?: boolean;
  account_type?: string;
  should_show_tos_footer?: boolean;
  scraper_execution_id?: string;
  scraper_input?: string;
  from_already_integrated?: boolean;
  selective_sync_data?: SelectiveSyncConfigurationListRequest;
  linked_account_conditions?: LinkedAccountCondition[]; // Selective Sync V2 Fields, only used for already integrated
  // sync connection fields
  auth_fields?: Record<string, string>;
  schema_id?: string;
  sync_connection_id?: string;
  // manual upload fields
  report_file_ids?: undefined | boolean;
  validation_before_s2?: boolean;
};

export type Props = {
  apiBaseURL: string;
  linkToken: string;
  forAdmin: boolean;
  isLinkOpen: boolean;
  onLinkClose: () => void;
  setLinkToken: (linkToken: string | undefined) => void;
  setIsWaitingForReopenConfirmation: (isWaitingForReopenConfirmation: boolean) => void;
  initialPageResponseBody: any;
  isLinkTAndCHidden: boolean;
  shouldHideMergeLogo: boolean;
  shouldShowPoweredByMerge: boolean;
};

const MergeLinkModalRoot = ({
  isLinkTAndCHidden,
  onLinkClose,
  apiBaseURL,
  linkToken,
  forAdmin,
  isLinkOpen,
  setLinkToken,
  setIsWaitingForReopenConfirmation,
  initialPageResponseBody,
  shouldHideMergeLogo,
  shouldShowPoweredByMerge,
}: Props) => {
  const [linkState, setLinkState] = useState({
    currentPage: LinkingFlowPageEnum.LINKING_FLOW_INITIAL_STEP,
    currentLinkPayload: {} as NextPagePayloadInfo,
    translatedLanguageData: {} as { [key: string]: string },
  });
  const [modalSize, setModalSize] = useState<"sm" | "lg">("sm");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasInitialResponse, setHasInitialResponse] = useState<boolean>(false);
  const [customLoadingText, setCustomLoadingText] = useState<string>("");
  const [customLoadingSubText, setCustomLoadingSubText] = useState<string>("");
  const [stateHistory, setStateHistory] = useState<StateHistoryRecord[]>([]);
  const [linkedAccountID, setLinkedAccountID] = useState<string>();
  const [persistedCustomization, setPersistedCustomization] =
    useState<MergeLinkOrganizationCustomizationData | null>(null);

  const payloadCustomization =
    linkState.currentLinkPayload?.serialized_organization_customization;

  // Carry the org customization forward across pages whose payload omits it
  useEffect(() => {
    if (!payloadCustomization) return;
    setPersistedCustomization((prev) => mergeOrgCustomization(prev, payloadCustomization));
  }, [payloadCustomization]);

  // Merged during render as well as in the effect, so the first payload's styles
  // apply on the same frame rather than flashing the defaults first
  const orgCustomization = payloadCustomization
    ? mergeOrgCustomization(persistedCustomization, payloadCustomization)
    : persistedCustomization;

  const modalBorderRadius = orgCustomization?.modal_border_radius ?? null;
  const coBrandingLogo = orgCustomization?.show_cobranding
    ? orgCustomization.logo ?? null
    : null;

  const translate = (keyString: string, fallbackString: string) => {
    if (keyString in (linkState.translatedLanguageData ?? {})) {
      return linkState.translatedLanguageData[keyString];
    } else {
      return fallbackString ?? "";
    }
  };

  const navigateToPreviousEntryInHistory = (linkedAccountFormData?: LinkedAccountFormData, registeredUserFormData?: RegisteredUserFormData) => {
    const backScreensToSkip = [LinkingFlowPageEnum.LINKING_FLOW_LINKING_LOADING];
    let currStateHistory = stateHistory;
    let previousHistoryRecord = currStateHistory[currStateHistory.length - 1];
    currStateHistory = currStateHistory.slice(0, currStateHistory.length - 1);

    while (previousHistoryRecord && backScreensToSkip.includes(previousHistoryRecord.page)) {
      previousHistoryRecord = currStateHistory[currStateHistory.length - 1];
      currStateHistory = currStateHistory.slice(0, currStateHistory.length - 1);
    }

    if (previousHistoryRecord) {
      let currentLinkPayload: NextPagePayloadInfo = previousHistoryRecord.payload;

      // Preserve linked account form data if provided
      if (linkedAccountFormData) {
        currentLinkPayload = {
          ...currentLinkPayload,
          linked_account_form_data: linkedAccountFormData,
        };
      }

      // Preserve registered user form data if provided (for credential persistence)
      if (registeredUserFormData) {
        currentLinkPayload = {
          ...currentLinkPayload,
          registered_user_form_data: registeredUserFormData,
        };
      }

      setLinkState({
        currentPage: previousHistoryRecord.page,
        currentLinkPayload: currentLinkPayload,
        translatedLanguageData: previousHistoryRecord.translatedLanguageData ?? {},
      });
      setStateHistory(currStateHistory);
    }
  };

  const findPreviousPagePayloadInHistory = (pageToFind: LinkingFlowPageEnum) => {
    return stateHistory
      .slice()
      .reverse()
      .find((stateHistoryRecord) => stateHistoryRecord.page === pageToFind)?.payload;
  };

  const getNextPageAndSetPage = (
    formData: RegisteredUserFlowFormData,
    currentPageOverride?: LinkingFlowPageEnum
  ) => {
    setIsLoading(true);

    const onGetNextPageResponse = (response: NextPageResponse) => {
      setCustomLoadingText("");
      setCustomLoadingSubText("");

      // The org opted out of the success screen, whose button only exits anyway.
      // Exiting here still hands the parent SEND_SUCCESS before CLOSE_LINKING_FLOW,
      // and isLoading stays true so the loading screen holds until the modal closes
      const shouldAutoExitOnSuccess =
        (response.linking_flow_payload?.link_was_successful ?? false) &&
        (response.linking_flow_payload?.should_auto_exit ?? false) &&
        !(response.linking_flow_payload?.should_call_next_page ?? false);

      if (shouldAutoExitOnSuccess) {
        // The flow completed, it just never renders SUCCESS: this branch exits
        // before setLinkState, so useStepTelemetry never sees the step. Publish
        // it here, or every org that opted out of the success screen reports
        // zero completions.
        setLinkingFlowContext({
          step: LinkingFlowPageEnum.LINKING_FLOW_SUCCESS,
          connector: response.linking_flow_payload?.connector?.name,
        });
        capturePageview({ link_was_successful: true });

        passSuccess(response.linking_flow_payload.authorization_code);
        setHasInitialResponse(true);
        onExit();
        return;
      }

      const newHistoryRecord: StateHistoryRecord = {
        page: linkState.currentPage,
        payload: {
          ...linkState.currentLinkPayload,
          // Preserve submitted credentials in history so they persist when navigating back
          ...(formData.registered_user_form_data && {
            registered_user_form_data: formData.registered_user_form_data,
          }),
        },
        translatedLanguageData: linkState.translatedLanguageData,
      };
      // Using currentPageOverride to determine if we should reset the history on initial step
      if (currentPageOverride === LinkingFlowPageEnum.LINKING_FLOW_INITIAL_STEP) {
        setStateHistory([newHistoryRecord]);
      } else {
        setStateHistory((prevHistory) => [...prevHistory, newHistoryRecord]);
      }
      setLinkState({
        currentPage: response.next_step as LinkingFlowPageEnum,
        currentLinkPayload: response.linking_flow_payload ?? ({} as NextPagePayloadInfo),
        translatedLanguageData: response.translated_language_data ?? {},
      });
      if (response.linking_flow_payload?.link_was_successful) {
        // Pass authorization code if present in payload (for secure magic link flow)
        passSuccess(response.linking_flow_payload.authorization_code);
      }
      setIsLoading(false);
      setHasInitialResponse(true);
    };

    if (initialPageResponseBody) {
      onGetNextPageResponse(initialPageResponseBody);
      return;
    }

    getNextPage({
      apiBaseURL,
      linkToken,
      formData,
      forAdmin,
      currentPage: currentPageOverride ?? linkState.currentPage,
      onResponse: onGetNextPageResponse,
      onError: (error: any) => {
        if (
          (process.env.REACT_APP_MERGE_ENV === "PRODUCTION" ||
            process.env.REACT_APP_MERGE_ENV === "DEVELOP") &&
          linkState.currentPage === LinkingFlowPageEnum.LINKING_FLOW_LINKING_LOADING
        ) {
          captureException(error);
        }
        setIsLoading(false);
        setHasInitialResponse(true);
        setLinkState({
          currentPage: LinkingFlowPageEnum.LINKING_FLOW_EXPIRED_TOKEN,
          currentLinkPayload: {},
          translatedLanguageData: {},
        });
      },
    });
  };

  useEffect(() => {
    const loadingPages = [LinkingFlowPageEnum.LINKING_FLOW_LINKING_LOADING];
    if (loadingPages.includes(linkState.currentPage)) {
      if (linkState.currentPage === LinkingFlowPageEnum.LINKING_FLOW_LINKING_LOADING) {
        const loadingText = linkState.currentLinkPayload.validate_before_s2
          ? translate("linking-loading-text-validate-permissions", "Validating your permissions")
          : translate("linking-loading-text", "Setting up your account");
        setCustomLoadingText(loadingText);
        setCustomLoadingSubText(translate("linking-loading-subtext", "This might take a while!"));
        getNextPageAndSetPage({
          registered_user_form_data: linkState.currentLinkPayload.registered_user_form_data,
          selected_connector: linkState.currentLinkPayload.connector,
        });
      }
    }
  }, [linkState]);

  useEffect(() => {
    // The reason we wait for filePickerConfigSent is that this is the only variable being passed into initial link and its being passed from the users
    // Link embedding itself. It gets set at the same time as linktoken so it wont be any slower to wait for both and we need an accurate value
    // of this to determine next page
    getNextPageAndSetPage(
      {},
      // Make sure to pass the initial page step as the current page override for proper history management
      LinkingFlowPageEnum.LINKING_FLOW_INITIAL_STEP
    );
  }, [linkToken, apiBaseURL]);

  const exitMergeLink = () => {
    // The iframe is only hidden on close, never torn down, so the step has to be
    // cleared by hand. Otherwise reopening onto the same step emits no pageview
    // and the top of the funnel loses the reopen.
    resetStepTracking();

    closeLinkingFlowWithoutPassingLinkToken(setIsWaitingForReopenConfirmation);

    if (apiBaseURL) {
      logLinkExecutionRecord({
        apiBaseURL,
        linkToken,
        eventName: LinkingFlowActionEnum.LINKING_FLOW_CLOSED,
        onSuccess: () => {},
      });
    }

    setLinkToken(undefined);
    onLinkClose();
  };

  const onExit = () => {
    if (linkedAccountID) {
      endLinkingFlowAndKickoffInitialSync({
        apiBaseURL,
        linkToken,
        onResponse: () => exitMergeLink(),
        onError: () => exitMergeLink(),
      });
    } else {
      exitMergeLink();
    }
  };

  // Don't render until the first API response arrives with customization data
  // to prevent a flash of default styles before custom styles are injected
  if (!hasInitialResponse) return null;

  return (
    <>
      <AgentHandlerLinkModalContext.Provider
        value={{
          modalSize,
          setModalSize,
          apiBaseURL,
          linkToken,
          allowNavigateToPreviousEntryInHistory: stateHistory.length > 1,
          navigateToPreviousEntryInHistory,
          getNextPageAndSetPage,
          setIsWaitingForReopenConfirmation,
          isLinkOpen,
          onExit,
          setLinkedAccountID,
          findPreviousPagePayloadInHistory,
          shouldHideMergeLogo,
          shouldShowPoweredByMerge,
          isLinkTAndCHidden,
          modalBorderRadius,
          coBrandingLogo,
          translate,
        }}
      >
        <PageSwitcher
          isLoading={isLoading}
          customLoadingText={customLoadingText}
          customLoadingSubText={customLoadingSubText}
          currentPage={linkState.currentPage}
          currentLinkPayload={linkState.currentLinkPayload}
          currentLinkTranslatedLanguageData={linkState.translatedLanguageData}
        />
      </AgentHandlerLinkModalContext.Provider>
      <CustomGlobalStyleWrapper orgCustomization={orgCustomization} />
    </>
  );
};

export default MergeLinkModalRoot;
